import { PanoramaFlight } from "@nova/protobuf"
import { selectPanoramaFlightsMap } from "features/entity-caching/flights/selectors"
import { selectAllPanoramaImagesAndUploads } from "features/entity-caching/images/panorama/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { selectActiveSidePanelModal } from "panels/content/side-panel/slice"
import { activateSidePanelModal } from "panels/content/side-panel/thunks"
import { PanoramaState } from "plugins/Panorama/PanoramaSlice"
import { PluginSpec, staticIcon } from "plugins/plugin-registry"
import { useMemo } from "react"

export const getUsePanoramaMapNodes = (spec: PluginSpec<PanoramaFlight>) => {
    const usePanoramaMapNodes = () => {
        const activeSidePanelModalId = useAppSelector(selectActiveSidePanelModal())?.id
        const allPanoramaImages = useAppSelector(
            selectAllPanoramaImagesAndUploads
        ) as PanoramaState[]
        const allPanosMap = useAppSelector(selectPanoramaFlightsMap)
        const entityData = useMemo(() => {
            return [
                ...Object.values(allPanosMap)
                    .filter(
                        (pano) =>
                            !!pano.images.length &&
                            !pano.archivedTimestamp &&
                            pano.images[0].longitude &&
                            pano.images[0].latitude
                    )
                    .map((entity) => {
                        return getNodeData({
                            entity,
                            lat: entity.images[0].latitude!,
                            lng: entity.images[0].longitude!,
                            activeSidePanelModalId,
                        })
                    }),
                ...allPanoramaImages
                    .filter((image) => {
                        if (!image.flightId || !!allPanosMap[image.flightId]?.images?.length)
                            return false
                        const entity = spec.selectSingleEntity(image.flightId)(store.getState())
                        return (
                            !!entity &&
                            !entity.archivedTimestamp &&
                            image.longitude &&
                            image.latitude
                        )
                    })
                    .map((image) => {
                        const entity = spec.selectSingleEntity(image.flightId!)(store.getState())
                        return getNodeData({
                            entity,
                            lat: image.latitude!,
                            lng: image.longitude!,
                            activeSidePanelModalId,
                        })
                    }),
            ]
        }, [activeSidePanelModalId, allPanoramaImages, allPanosMap])
        return entityData
    }
    return usePanoramaMapNodes
}

function getNodeData<T extends { id: string; visible: boolean; locked?: boolean }>({
    entity,
    lat,
    lng,
    activeSidePanelModalId,
}: {
    entity: T
    lat: number
    lng: number
    activeSidePanelModalId: string | undefined
}) {
    return {
        id: entity.id,
        position: {
            lat,
            lng,
        },
        visible: entity.visible,
        locked: !!entity.locked,
        active: activeSidePanelModalId === entity.id,
        onClick: () => {
            store.dispatch(
                activateSidePanelModal({
                    id: entity.id,
                    type: "layer",
                    layerContentType: "pano",
                })
            )
        },

        iconSrc: staticIcon({
            default: "/icons/panorama/normal.svg",
            hover: "/icons/panorama/hover.svg",
            active: "/icons/panorama/active.svg",
        }),
    }
}
